import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Grid, Download } from "lucide-react";
import { downloadCsv, today } from "@/lib/csv";

const MODES = [
    { value: "brudt", label: "Brudte løfter", unit: "%" },
    { value: "skandale", label: "Skandaler", unit: "" },
    { value: "løgn", label: "Løgne", unit: "" },
    { value: "valgflæsk", label: "Valgflæsk", unit: "" },
];

const MODE_META = {
    brudt: {
        title: "Hvor brydes der flest løfter?",
        subtitle: "Andelen af brudte løfter pr. kategori pr. parti. Mørkere rød = flere løftebrud.",
        thresholds: [0, 33, 66],
    },
    skandale: {
        title: "Hvor er der flest dokumenterede skandaler?",
        subtitle: "Antal dokumenterede skandaler (rigsret, magtmisbrug, bilagssager mv.) pr. kategori.",
        thresholds: [0, 1, 2],
    },
    løgn: {
        title: "Hvor tages politikerne flest gange i løgn?",
        subtitle: "Antal dokumenterede løgne fra mediearkivet, fordelt pr. kategori.",
        thresholds: [0, 1, 2],
    },
    valgflæsk: {
        title: "Hvor serveres der mest valgflæsk?",
        subtitle: "Antal valgflæsk-sager i mediearkivet, fordelt pr. kategori.",
        thresholds: [0, 1, 2],
    },
};

export default function CategoryHeatmap() {
    const [mode, setMode] = useState("brudt");
    const [data, setData] = useState(null);

    useEffect(() => {
        setData(null);
        api
            .get("/stats/category-heatmap", { params: { mode } })
            .then((r) => setData(r.data));
    }, [mode]);

    const meta = MODE_META[mode];

    const getColor = (value) => {
        if (value === null || value === undefined) return "#F0F2F7";
        const [t0, t1, t2] = meta.thresholds;
        if (value <= t0) return "#DCF5E4";
        if (value <= t1) return "#FFE8C2";
        if (value <= t2) return "#F8A5A5";
        return "#BB1B2D";
    };

    const getTextColor = (value) => {
        if (value === null || value === undefined) return "#4F5A7A";
        const [, , t2] = meta.thresholds;
        return value > t2 ? "#ffffff" : "#0B1A3A";
    };

    const currentModeMeta = MODES.find((m) => m.value === mode);

    const downloadCsvFile = () => {
        if (!data) return;
        const unit = mode === "brudt" ? " %" : "";
        const rows = [];
        const header = ["Parti", "Kortnavn", ...data.categories];
        if (mode === "brudt") header.push("Samlet brudt-andel");
        rows.push(header);
        data.parties_matrix.forEach((row) => {
            const r = [row.party_name, row.short_name];
            let totalBrudt = 0;
            let totalAll = 0;
            data.categories.forEach((cat) => {
                const cell = row.cells[cat];
                if (!cell || cell.value === null || cell.value === undefined) {
                    r.push("");
                } else if (mode === "brudt") {
                    r.push(`${cell.value}${unit} (${cell.brudt}/${cell.total})`);
                    totalBrudt += cell.brudt || 0;
                    totalAll += cell.total || 0;
                } else {
                    r.push(cell.value);
                }
            });
            if (mode === "brudt") {
                r.push(totalAll ? `${Math.round(100 * totalBrudt / totalAll)} %` : "");
            }
            rows.push(r);
        });
        downloadCsv(rows, `loftebarometer-heatmap-${mode}-${today()}.csv`);
    };

    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8" data-testid="category-heatmap">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Grid className="w-5 h-5 text-royal" />
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Kategori-heatmap
                        </span>
                    </div>
                    <h2 className="font-serif-dm text-2xl md:text-3xl text-royal">{meta.title}</h2>
                    <p className="text-sm text-[#4F5A7A] mt-1">{meta.subtitle}</p>
                </div>
                <div className="flex items-center gap-2 self-start md:self-end flex-wrap">
                    <div className="flex gap-1" data-testid="heatmap-mode-toggle">
                        {MODES.map((m) => (
                            <button
                                key={m.value}
                                onClick={() => setMode(m.value)}
                                data-testid={`heatmap-mode-${m.value}`}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                    mode === m.value
                                        ? "bg-royal text-white"
                                        : "bg-[#F0F2F7] text-[#4F5A7A] hover:bg-[#E5E8F0]"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={downloadCsv}
                        disabled={!data}
                        data-testid="heatmap-csv-download"
                        title="Download som CSV til Excel / Google Sheets"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-white border border-[#E5E8F0] text-royal hover:bg-royal-soft transition-colors disabled:opacity-40"
                    >
                        <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                </div>
            </div>

            {!data ? (
                <p className="text-sm text-[#4F5A7A] py-10 text-center">Indlæser heatmap...</p>
            ) : (
                <>
                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-3 text-xs mb-5">
                        <span className="text-[#4F5A7A] font-semibold uppercase tracking-wider">
                            Farveskala:
                        </span>
                        <LegendItem color="#DCF5E4" label={mode === "brudt" ? "0 %" : "0"} />
                        <LegendItem color="#FFE8C2" label={mode === "brudt" ? "1-33 %" : "1"} />
                        <LegendItem color="#F8A5A5" label={mode === "brudt" ? "34-66 %" : "2"} />
                        <LegendItem
                            color="#BB1B2D"
                            label={mode === "brudt" ? "67-100 %" : "3+"}
                            textWhite
                        />
                        <LegendItem color="#F0F2F7" label="Ingen data" />
                    </div>

                    {/* Heatmap */}
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="heatmap-table">
                            <thead>
                                <tr>
                                    <th className="text-left p-2 text-xs uppercase tracking-wider text-[#4F5A7A] font-semibold sticky left-0 bg-white">
                                        Parti
                                    </th>
                                    {data.categories.map((cat) => (
                                        <th
                                            key={cat}
                                            className="p-2 text-xs text-[#4F5A7A] font-semibold text-center min-w-[80px]"
                                        >
                                            <div className="vertical-label" title={cat}>
                                                {cat}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.parties_matrix.map((row) => (
                                    <tr key={row.party_id}>
                                        <td className="p-2 sticky left-0 bg-white border-t border-[#EEF0F7]">
                                            <Link
                                                to={`/partier/${row.party_id}`}
                                                className="flex items-center gap-2 hover:text-danish-red transition-colors"
                                            >
                                                <span
                                                    className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                                    style={{ backgroundColor: row.color }}
                                                >
                                                    {row.short_name}
                                                </span>
                                                <span className="font-semibold text-royal text-sm whitespace-nowrap">
                                                    {row.party_name}
                                                </span>
                                            </Link>
                                        </td>
                                        {data.categories.map((cat) => {
                                            const cell = row.cells[cat] || {};
                                            const value = cell.value;
                                            const hasData = value !== null && value !== undefined;
                                            const tooltip = buildTooltip(row.party_name, cat, mode, cell);
                                            return (
                                                <td
                                                    key={cat}
                                                    className="p-1 border-t border-[#EEF0F7]"
                                                    data-testid={`cell-${row.short_name}-${cat}`}
                                                >
                                                    <div
                                                        className="rounded-md h-12 flex flex-col items-center justify-center text-xs font-semibold leading-tight"
                                                        style={{
                                                            backgroundColor: getColor(hasData ? value : null),
                                                            color: getTextColor(hasData ? value : null),
                                                        }}
                                                        title={tooltip}
                                                    >
                                                        {hasData ? (
                                                            <>
                                                                <span>
                                                                    {value}
                                                                    {currentModeMeta.unit}
                                                                </span>
                                                                {mode === "brudt" && cell.total > 0 && (
                                                                    <span className="text-[10px] opacity-70">
                                                                        {cell.brudt}/{cell.total}
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-[10px] opacity-50">—</span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function buildTooltip(party, cat, mode, cell) {
    if (!cell || cell.value === null || cell.value === undefined) {
        return mode === "brudt"
            ? "Ingen løfter i denne kategori"
            : `Ingen ${mode}-sager registreret`;
    }
    if (mode === "brudt") {
        return `${party} · ${cat}\n${cell.brudt}/${cell.total} brudt (${cell.value} %)\n${cell.holdt} holdt, ${cell.delvist} delvist`;
    }
    return `${party} · ${cat}\n${cell.value} ${mode}-sag${cell.value === 1 ? "" : "er"}`;
}

function LegendItem({ color, label, textWhite }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="w-4 h-4 rounded"
                style={{ backgroundColor: color, color: textWhite ? "#fff" : "#0B1A3A" }}
            />
            {label}
        </span>
    );
}
